import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Send,
  MessageSquare,
  ArrowLeft,
  MapPin,
  Phone,
  Check,
  CheckCheck,
  Loader2,
  AlertCircle,
  Briefcase,
  ShieldCheck,
  Search,
  Paperclip,
  X,
  Users,
  Lock,
  ExternalLink,
  Sparkles,
  Wrench,
  UserCheck,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { Message, Job, EligibleChatContact, Profile } from '../../types';
import {
  checkJobPaymentStatus,
  cleanContactPhone,
  compressImageFile,
  getEligibleChatContacts,
  isFakeAccountProfile,
  uploadJobMedia,
} from '../../lib/supabaseService';
import { VerificationBadge } from '../../components/common/VerificationBadge';

function isProfileVerified(p?: Partial<Profile> | null): boolean {
  if (!p) return false;
  return (
    p.verificationStatus === 'verified' ||
    p.kycStatus === 'VERIFIED' ||
    p.professionalApprovalStatus === 'approved'
  );
}

interface ConversationThreadItem {
  contactId: string;
  contactName: string;
  contactAvatar: string;
  contactRole: 'customer' | 'professional' | 'admin';
  businessName?: string;
  primaryCategory?: string;
  secondaryCategories?: string[];
  state?: string;
  lga?: string;
  cityArea?: string;
  verificationStatus?: string;
  isVerified: boolean;
  isAvailable: boolean;
  isOnline: boolean;
  lastSeenAt?: string;
  rating?: number;
  reviewCount?: number;
  phone?: string;
  phoneProtected: boolean;
  conversationId?: string;
  sharedJobs: Job[];
  primaryJob?: Job;
  lastMessage?: Message;
  lastActivityAt: string;
  unreadCount: number;
  hasHistory: boolean;
}

function formatChatListTime(iso?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  if (isToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();
  if (isYesterday) return 'Yesterday';
  return date.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

function formatMessageDateDivider(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  if (
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  ) {
    return 'Today';
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  ) {
    return 'Yesterday';
  }
  return date.toLocaleDateString([], {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export const MessagesPage: React.FC = () => {
  const {
    messages,
    sendMessage,
    fetchMessages,
    markMessagesRead,
    currentUser,
    jobs,
    quotes,
    profiles,
    viewParams,
    navigateTo,
    refreshJobsAndQuotes,
    onlineUserIds,
  } = useApp();

  const isCustomer = currentUser?.role === 'customer';
  const isPro = currentUser?.role === 'professional';
  const isAdmin = currentUser?.role === 'admin';

  // Directory contacts fetched from /api/messages/contacts
  const [directoryContacts, setDirectoryContacts] = useState<EligibleChatContact[]>([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState<boolean>(true);

  // Search & Sidebar tab state
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sidebarTab, setSidebarTab] = useState<'all' | 'unread' | 'contacts'>('all');

  // Active conversation selection: tracked by contactId and optional selectedJobId
  const [activeContactId, setActiveContactId] = useState<string>('');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(viewParams.jobId || null);

  // Mobile responsive view: 'list' (contact list screen) vs 'chat' (active chat screen)
  const [mobileView, setMobileView] = useState<'list' | 'chat'>(
    Boolean(viewParams.jobId || viewParams.contactId || viewParams.proId) ? 'chat' : 'list'
  );

  // Message composer state
  const [inputText, setInputText] = useState<string>('');
  const [pendingAttachments, setPendingAttachments] = useState<string[]>([]);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // 1. Filter jobs accessible to the current user (including active, completed, and cancelled jobs)
  const accessibleJobs = useMemo(() => {
    if (!currentUser) return [];
    return jobs.filter((job) => {
      if (isAdmin) return true;
      if (isCustomer) {
        return job.customerId === currentUser.id || job.customerPhone === currentUser.phone;
      }
      if (isPro) {
        const meta = (job.aiScopeEstimate as any)?.lifecycle_meta || {};
        const assignedProId =
          job.selectedProfessionalId ||
          (job as any).selected_professional_id ||
          (job as any).professional_id ||
          meta.selected_professional_id ||
          meta.assigned_professional_id ||
          meta.target_professional_id;
        const isAssigned = assignedProId === currentUser.id;
        const hasAcceptedQuote = quotes.some(
          (q) =>
            q.jobId === job.id &&
            q.professionalId === currentUser.id &&
            q.status === 'accepted'
        );
        const hasMessagesOnJob = messages.some(
          (m) =>
            m.jobId === job.id &&
            (m.senderId === currentUser.id || m.receiverId === currentUser.id)
        );
        return isAssigned || hasAcceptedQuote || hasMessagesOnJob;
      }
      return false;
    });
  }, [jobs, quotes, messages, currentUser, isAdmin, isCustomer, isPro]);

  const isUnauthorizedJob = Boolean(
    viewParams.jobId &&
      !accessibleJobs.some((j) => j.id === viewParams.jobId) &&
      !isAdmin
  );

  // Load eligible contacts and user's full message history on mount
  const loadContactsAndHistory = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const [contactsList] = await Promise.all([
        getEligibleChatContacts(currentUser.id),
        fetchMessages(undefined, undefined),
      ]);
      setDirectoryContacts(contactsList);
    } catch (err) {
      console.warn('Failed to load eligible messaging contacts:', err);
    } finally {
      setIsLoadingContacts(false);
    }
  }, [currentUser?.id, fetchMessages]);

  useEffect(() => {
    loadContactsAndHistory();
    const interval = setInterval(() => {
      if (currentUser?.id) {
        getEligibleChatContacts(currentUser.id).then((list) => {
          setDirectoryContacts(list);
        });
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [loadContactsAndHistory, currentUser?.id]);

  // Build unified map of ONLY eligible contacts (hired, actively working with, or previously chatted with)
  const unifiedThreads = useMemo<ConversationThreadItem[]>(() => {
    if (!currentUser) return [];

    const map = new Map<string, ConversationThreadItem>();

    const upsertContact = (
      id: string,
      partial: Partial<ConversationThreadItem> & { contactName?: string }
    ) => {
      if (!id || id === currentUser.id) return;
      const existing = map.get(id);
      const isRealtimeOnline = onlineUserIds.includes(id);

      if (!existing) {
        map.set(id, {
          contactId: id,
          contactName: partial.contactName || 'JobMatch User',
          contactAvatar: partial.contactAvatar || '',
          contactRole: partial.contactRole || (isCustomer ? 'professional' : 'customer'),
          businessName: partial.businessName,
          primaryCategory: partial.primaryCategory,
          secondaryCategories: partial.secondaryCategories || [],
          state: partial.state,
          lga: partial.lga,
          cityArea: partial.cityArea,
          verificationStatus: partial.verificationStatus,
          isVerified: Boolean(partial.isVerified),
          isAvailable: partial.isAvailable !== false,
          isOnline: Boolean(isRealtimeOnline || partial.isOnline),
          lastSeenAt: partial.lastSeenAt,
          rating: partial.rating,
          reviewCount: partial.reviewCount,
          phone: partial.phone,
          phoneProtected: partial.phoneProtected !== false,
          conversationId: partial.conversationId,
          sharedJobs: partial.sharedJobs || [],
          primaryJob: partial.primaryJob,
          lastMessage: partial.lastMessage,
          lastActivityAt: partial.lastActivityAt || '',
          unreadCount: partial.unreadCount || 0,
          hasHistory: Boolean(partial.hasHistory),
        });
      } else {
        map.set(id, {
          ...existing,
          contactName:
            existing.contactName && existing.contactName !== 'JobMatch User'
              ? existing.contactName
              : partial.contactName || existing.contactName,
          contactAvatar: existing.contactAvatar || partial.contactAvatar || '',
          contactRole: partial.contactRole || existing.contactRole,
          businessName: existing.businessName || partial.businessName,
          primaryCategory: existing.primaryCategory || partial.primaryCategory,
          secondaryCategories:
            existing.secondaryCategories && existing.secondaryCategories.length > 0
              ? existing.secondaryCategories
              : partial.secondaryCategories || [],
          state: existing.state || partial.state,
          lga: existing.lga || partial.lga,
          cityArea: existing.cityArea || partial.cityArea,
          verificationStatus: existing.verificationStatus || partial.verificationStatus,
          isVerified: existing.isVerified || Boolean(partial.isVerified),
          isAvailable:
            partial.isAvailable !== undefined ? partial.isAvailable : existing.isAvailable,
          isOnline: Boolean(isRealtimeOnline || existing.isOnline || partial.isOnline),
          lastSeenAt: partial.lastSeenAt || existing.lastSeenAt,
          rating: existing.rating ?? partial.rating,
          reviewCount: existing.reviewCount ?? partial.reviewCount,
          phone: existing.phone || partial.phone,
          phoneProtected:
            partial.phoneProtected === false ? false : existing.phoneProtected,
          conversationId: existing.conversationId || partial.conversationId,
          lastActivityAt:
            partial.lastActivityAt && partial.lastActivityAt > existing.lastActivityAt
              ? partial.lastActivityAt
              : existing.lastActivityAt,
          hasHistory: existing.hasHistory || Boolean(partial.hasHistory),
        });
      }
    };

    // A. Seed from backend /api/messages/contacts (strictly scoped to hired, working, or previously chatted contacts)
    for (const c of directoryContacts) {
      if (!c?.id || c.id === currentUser.id) continue;
      if (!isAdmin && !c.conversationId) {
        if (isCustomer && c.role !== 'professional') continue;
        if (isPro && c.role !== 'customer') continue;
      }

      upsertContact(c.id, {
        contactName: c.fullName || c.businessName || 'Verified User',
        contactAvatar: c.avatarUrl || '',
        contactRole: c.role,
        businessName: c.businessName,
        primaryCategory: c.primaryCategory,
        secondaryCategories: c.secondaryCategories,
        state: c.state,
        lga: c.lga,
        cityArea: c.cityArea,
        verificationStatus: c.verificationStatus,
        isVerified: c.verificationStatus === 'verified' || c.role === 'customer',
        isAvailable: c.isAvailable !== false,
        isOnline: Boolean(c.online),
        lastSeenAt: c.lastSeenAt,
        rating: c.rating,
        reviewCount: c.reviewCount,
        phone: c.phone,
        phoneProtected: c.phoneProtected !== false,
        conversationId: c.conversationId,
        lastActivityAt: c.lastMessageAt || '',
        hasHistory: Boolean(c.conversationId),
      });
    }

    // B. Link hired/working jobs (including active, completed, and cancelled jobs) to their counterparty
    for (const job of accessibleJobs) {
      const jobQuotes = quotes.filter((q) => q.jobId === job.id);
      const acceptedQuote = jobQuotes.find((q) => q.status === 'accepted');
      const meta = (job.aiScopeEstimate as any)?.lifecycle_meta || {};
      const hiredProId =
        job.selectedProfessionalId ||
        (job as any).selected_professional_id ||
        (job as any).professional_id ||
        meta.selected_professional_id ||
        meta.assigned_professional_id ||
        meta.target_professional_id ||
        acceptedQuote?.professionalId ||
        '';

      let counterpartyId = '';
      let counterpartyName = '';
      let counterpartyAvatar = '';
      let counterpartyRole: 'customer' | 'professional' = 'professional';
      let counterpartyPhone = '';

      if (isCustomer) {
        counterpartyId = hiredProId;
        if (!counterpartyId) continue;
        const proProfile = profiles.find((p) => p.id === counterpartyId);
        counterpartyName =
          acceptedQuote?.professionalName ||
          job.selectedProfessionalName ||
          proProfile?.fullName ||
          proProfile?.businessName ||
          'Verified Artisan';
        counterpartyAvatar =
          proProfile?.avatarUrl || acceptedQuote?.professionalAvatar || '';
        counterpartyRole = 'professional';
        counterpartyPhone =
          cleanContactPhone(proProfile?.phone) ||
          cleanContactPhone(acceptedQuote?.professionalPhone) ||
          cleanContactPhone(job.selectedProfessionalPhone) ||
          '';
      } else {
        // Professional only sees the customer if hired/assigned/accepted quote or existing messages on the job
        const hasJobMessages = messages.some(
          (m) =>
            m.jobId === job.id &&
            (m.senderId === currentUser.id || m.receiverId === currentUser.id)
        );
        if (hiredProId !== currentUser.id && !hasJobMessages && !isAdmin) continue;
        counterpartyId = job.customerId;
        const custProfile = profiles.find((p) => p.id === counterpartyId);
        counterpartyName =
          job.customerName && job.customerName !== 'Customer'
            ? job.customerName
            : custProfile?.fullName || 'Customer';
        counterpartyAvatar = custProfile?.avatarUrl || '';
        counterpartyRole = 'customer';
        counterpartyPhone =
          cleanContactPhone(job.customerPhone) ||
          cleanContactPhone(custProfile?.phone) ||
          '';
      }

      if (counterpartyId && counterpartyId !== currentUser.id) {
        const proProfile = profiles.find((p) => p.id === counterpartyId);
        if (proProfile && isFakeAccountProfile(proProfile)) continue;

        const isAssignedOrPaid =
          Boolean(
            hiredProId &&
              ['assigned', 'in_progress', 'completed', 'completed_by_professional'].includes(
                job.status
              )
          ) ||
          Boolean(job.payment_released) ||
          job.payment_status === 'held_in_escrow' ||
          job.payment_status === 'released' ||
          job.paymentStatus === 'paid';

        upsertContact(counterpartyId, {
          contactName: counterpartyName,
          contactAvatar: counterpartyAvatar,
          contactRole: counterpartyRole,
          businessName: proProfile?.businessName,
          primaryCategory:
            proProfile?.primaryCategory || (isCustomer ? job.categoryName : undefined),
          secondaryCategories: proProfile?.secondaryCategories,
          state: proProfile?.state || job.state,
          lga: proProfile?.lga || job.lga,
          cityArea: proProfile?.cityArea,
          verificationStatus: proProfile?.verificationStatus || 'verified',
          isVerified: isProfileVerified(proProfile) || counterpartyRole === 'customer' || true,
          isAvailable: proProfile?.isAvailable !== false,
          isOnline: onlineUserIds.includes(counterpartyId) || proProfile?.isAvailable !== false,
          rating: proProfile?.rating,
          reviewCount: proProfile?.reviewCount,
          phone: isAssignedOrPaid ? counterpartyPhone : undefined,
          phoneProtected: !isAssignedOrPaid,
          lastActivityAt: job.updatedAt || job.createdAt,
          hasHistory: true,
        });

        const item = map.get(counterpartyId);
        if (item) {
          if (!item.sharedJobs.some((j) => j.id === job.id)) {
            item.sharedJobs.push(job);
          }
          if (!item.primaryJob || job.id === viewParams.jobId) {
            item.primaryJob = job;
          }
        }
      }
    }

    // C. Aggregate all previous messages by counterparty (preserves 100% of conversation history, including completed & cancelled jobs)
    for (const msg of messages) {
      const involvesMe =
        msg.senderId === currentUser.id || msg.receiverId === currentUser.id;
      if (!involvesMe && !isAdmin) continue;

      const counterpartyId =
        msg.senderId === currentUser.id ? msg.receiverId : msg.senderId;
      if (!counterpartyId || counterpartyId === currentUser.id) continue;

      const counterpartyProfile = profiles.find((p) => p.id === counterpartyId);
      const fallbackName =
        msg.senderId === counterpartyId
          ? msg.senderName || counterpartyProfile?.fullName || 'JobMatch User'
          : msg.receiverName ||
            counterpartyProfile?.fullName ||
            counterpartyProfile?.businessName ||
            'JobMatch User';
      const fallbackAvatar =
        msg.senderId === counterpartyId
          ? msg.senderAvatar || counterpartyProfile?.avatarUrl || ''
          : msg.receiverAvatar || counterpartyProfile?.avatarUrl || '';

      upsertContact(counterpartyId, {
        contactName: fallbackName,
        contactAvatar: fallbackAvatar,
        contactRole:
          counterpartyProfile?.role || (isCustomer ? 'professional' : 'customer'),
        businessName: counterpartyProfile?.businessName,
        primaryCategory: counterpartyProfile?.primaryCategory,
        secondaryCategories: counterpartyProfile?.secondaryCategories,
        state: counterpartyProfile?.state,
        lga: counterpartyProfile?.lga,
        cityArea: counterpartyProfile?.cityArea,
        verificationStatus: counterpartyProfile?.verificationStatus || 'verified',
        isVerified: isProfileVerified(counterpartyProfile) || !isCustomer,
        isAvailable: counterpartyProfile?.isAvailable !== false,
        conversationId: msg.conversationId,
        lastActivityAt: msg.createdAt,
        hasHistory: true,
      });

      const item = map.get(counterpartyId);
      if (item) {
        if (msg.jobId) {
          const linkedJob = jobs.find((j) => j.id === msg.jobId);
          if (linkedJob && !item.sharedJobs.some((j) => j.id === linkedJob.id)) {
            item.sharedJobs.push(linkedJob);
            if (!item.primaryJob) item.primaryJob = linkedJob;
          }
        }
        if (
          !item.lastMessage ||
          new Date(msg.createdAt).getTime() >=
            new Date(item.lastMessage.createdAt).getTime()
        ) {
          item.lastMessage = msg;
          item.lastActivityAt = msg.createdAt;
        }
        if (msg.conversationId && !item.conversationId) {
          item.conversationId = msg.conversationId;
        }
        if (msg.receiverId === currentUser.id && !msg.read) {
          item.unreadCount += 1;
        }
        item.hasHistory = true;
      }
    }

    return Array.from(map.values()).sort((a, b) => {
      if (Boolean(a.lastMessage) !== Boolean(b.lastMessage)) {
        return a.lastMessage ? -1 : 1;
      }
      if (a.hasHistory !== b.hasHistory) {
        return a.hasHistory ? -1 : 1;
      }
      const timeA = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : 0;
      const timeB = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0;
      if (timeA !== timeB) return timeB - timeA;
      if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
      return a.contactName.localeCompare(b.contactName);
    });
  }, [
    currentUser,
    directoryContacts,
    profiles,
    accessibleJobs,
    jobs,
    quotes,
    messages,
    onlineUserIds,
    isCustomer,
    isPro,
    isAdmin,
    viewParams.jobId,
  ]);

  // Resolve initial / deep-linked active contact and job from viewParams (restricted to eligible contacts)
  useEffect(() => {
    if (!currentUser) return;

    const targetContactId = viewParams.contactId || viewParams.proId;
    if (targetContactId && targetContactId !== currentUser.id) {
      const thread = unifiedThreads.find((t) => t.contactId === targetContactId);
      if (thread || isAdmin) {
        setActiveContactId(targetContactId);
        setMobileView('chat');
        if (viewParams.jobId && !isUnauthorizedJob) {
          setSelectedJobId(viewParams.jobId);
        } else {
          setSelectedJobId(thread?.primaryJob?.id || null);
        }
        return;
      }
    }

    if (viewParams.jobId && !isUnauthorizedJob) {
      const targetJob =
        accessibleJobs.find((j) => j.id === viewParams.jobId) ||
        (isAdmin ? jobs.find((j) => j.id === viewParams.jobId) : undefined);

      if (targetJob) {
        setSelectedJobId(targetJob.id);
        const jobQuotes = quotes.filter((q) => q.jobId === targetJob.id);
        const acceptedQuote = jobQuotes.find((q) => q.status === 'accepted');
        const meta = (targetJob.aiScopeEstimate as any)?.lifecycle_meta || {};
        const counterpartyId = isCustomer
          ? targetJob.selectedProfessionalId ||
            (targetJob as any).selected_professional_id ||
            meta.selected_professional_id ||
            acceptedQuote?.professionalId ||
            ''
          : targetJob.customerId;
        if (counterpartyId) {
          setActiveContactId(counterpartyId);
          setMobileView('chat');
        }
      }
    }

    if (viewParams.paymentStatus === 'success' || viewParams.jobId) {
      refreshJobsAndQuotes();
    }
  }, [
    viewParams.contactId,
    viewParams.proId,
    viewParams.jobId,
    viewParams.paymentStatus,
    isUnauthorizedJob,
    currentUser,
    accessibleJobs,
    jobs,
    quotes,
    isCustomer,
    isAdmin,
    refreshJobsAndQuotes,
    unifiedThreads,
  ]);

  // Default to first thread with history (or first eligible contact on desktop) when none selected
  useEffect(() => {
    if (!activeContactId && unifiedThreads.length > 0) {
      const firstWithHistory =
        unifiedThreads.find((t) => t.lastMessage || t.hasHistory) || unifiedThreads[0];
      if (firstWithHistory) {
        setActiveContactId(firstWithHistory.contactId);
        if (!selectedJobId && firstWithHistory.primaryJob) {
          setSelectedJobId(firstWithHistory.primaryJob.id);
        }
      }
    }
  }, [activeContactId, unifiedThreads, selectedJobId]);

  const activeThread = useMemo<ConversationThreadItem | undefined>(() => {
    if (!activeContactId) return undefined;
    const found = unifiedThreads.find((t) => t.contactId === activeContactId);
    if (found) return found;

    // Only administrators can open an unlinked profile thread directly
    if (isAdmin) {
      const fallbackProfile = profiles.find((p) => p.id === activeContactId);
      if (fallbackProfile) {
        return {
          contactId: fallbackProfile.id,
          contactName:
            fallbackProfile.fullName || fallbackProfile.businessName || 'JobMatch User',
          contactAvatar: fallbackProfile.avatarUrl || '',
          contactRole: fallbackProfile.role,
          businessName: fallbackProfile.businessName,
          primaryCategory: fallbackProfile.primaryCategory,
          secondaryCategories: fallbackProfile.secondaryCategories || [],
          state: fallbackProfile.state,
          lga: fallbackProfile.lga,
          cityArea: fallbackProfile.cityArea,
          verificationStatus: fallbackProfile.verificationStatus,
          isVerified: isProfileVerified(fallbackProfile),
          isAvailable: fallbackProfile.isAvailable !== false,
          isOnline: onlineUserIds.includes(fallbackProfile.id),
          rating: fallbackProfile.rating,
          reviewCount: fallbackProfile.reviewCount,
          phoneProtected: false,
          sharedJobs: [],
          lastActivityAt: '',
          unreadCount: 0,
          hasHistory: false,
        };
      }
    }
    return undefined;
  }, [activeContactId, unifiedThreads, profiles, onlineUserIds, isAdmin]);

  const activeJob = useMemo<Job | undefined>(() => {
    if (selectedJobId) {
      const found =
        accessibleJobs.find((j) => j.id === selectedJobId) ||
        (isAdmin ? jobs.find((j) => j.id === selectedJobId) : undefined);
      if (found) return found;
    }
    return activeThread?.primaryJob;
  }, [selectedJobId, accessibleJobs, jobs, isAdmin, activeThread]);

  const [isEscrowVerified, setIsEscrowVerified] = useState<boolean>(false);

  useEffect(() => {
    if (activeJob?.id) {
      checkJobPaymentStatus(activeJob.id).then((res) => {
        setIsEscrowVerified(Boolean(res.isPaid));
      });
    } else {
      setIsEscrowVerified(false);
    }
  }, [activeJob?.id]);

  // Poll & mark messages read for the active conversation
  useEffect(() => {
    if (!currentUser?.id || !activeContactId) return;

    fetchMessages(activeJob?.id || undefined, {
      withUserId: activeContactId,
      conversationId: activeThread?.conversationId,
    });
    markMessagesRead(activeJob?.id || null, currentUser.id, {
      conversationId: activeThread?.conversationId,
      senderId: activeContactId,
    });

    const interval = setInterval(() => {
      fetchMessages(activeJob?.id || undefined, {
        withUserId: activeContactId,
        conversationId: activeThread?.conversationId,
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [
    currentUser?.id,
    activeContactId,
    activeJob?.id,
    activeThread?.conversationId,
    fetchMessages,
    markMessagesRead,
  ]);

  const conversationMessages = useMemo(() => {
    if (!currentUser || !activeContactId) return [];

    return messages
      .filter((m) => {
        const isBetweenParticipants =
          (m.senderId === currentUser.id && m.receiverId === activeContactId) ||
          (m.senderId === activeContactId && m.receiverId === currentUser.id);

        if (isBetweenParticipants) {
          return true;
        }
        if (isAdmin && activeJob && m.jobId === activeJob.id) {
          return true;
        }
        return false;
      })
      .sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
  }, [messages, currentUser, activeContactId, isAdmin, activeJob]);

  useEffect(() => {
    if (!currentUser?.id || !activeContactId) return;
    const hasUnreadFromActive = conversationMessages.some(
      (m) => m.senderId === activeContactId && m.receiverId === currentUser.id && !m.read
    );
    if (hasUnreadFromActive) {
      markMessagesRead(activeJob?.id || null, currentUser.id, {
        conversationId: activeThread?.conversationId,
        senderId: activeContactId,
      });
    }
  }, [
    conversationMessages,
    currentUser?.id,
    activeContactId,
    activeJob?.id,
    activeThread?.conversationId,
    markMessagesRead,
  ]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationMessages.length, activeContactId]);

  const filteredThreads = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return unifiedThreads.filter((item) => {
      if (sidebarTab === 'unread' && item.unreadCount === 0) return false;

      if (!q) return true;

      const searchable = [
        item.contactName,
        item.businessName,
        item.primaryCategory,
        ...(item.secondaryCategories || []),
        item.state,
        item.lga,
        item.cityArea,
        item.contactRole,
        item.lastMessage?.content,
        ...item.sharedJobs.map((j) => `${j.title} ${j.categoryName} ${j.status}`),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchable.includes(q);
    });
  }, [unifiedThreads, searchQuery, sidebarTab]);

  const totalUnreadCount = useMemo(
    () => unifiedThreads.reduce((sum, t) => sum + t.unreadCount, 0),
    [unifiedThreads]
  );

  const handleSelectContact = (thread: ConversationThreadItem) => {
    setActiveContactId(thread.contactId);
    setSelectedJobId(thread.primaryJob?.id || null);
    setMobileView('chat');
    setSendError(null);
  };

  const handleAttachmentSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setIsUploadingAttachment(true);
    setSendError(null);

    try {
      const newUrls: string[] = [];
      for (const file of files.slice(0, 3)) {
        if (!file.type.startsWith('image/')) {
          setSendError('Only image attachments (JPG, PNG, WebP) are supported in chat.');
          continue;
        }
        const compressed = await compressImageFile(file, 200 * 1024);
        const uploadedUrl = await uploadJobMedia(
          compressed,
          activeJob?.id || 'chat-attachments'
        );
        if (uploadedUrl) {
          newUrls.push(uploadedUrl);
        }
      }
      setPendingAttachments((prev) => [...prev, ...newUrls].slice(0, 4));
    } catch (err: any) {
      setSendError(err?.message || 'Could not process image attachment.');
    } finally {
      setIsUploadingAttachment(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = inputText.trim();
    if ((!clean && pendingAttachments.length === 0) || isSending || !currentUser) return;

    if (!activeThread || !activeContactId) {
      setSendError('Please select a contact from the list to start chatting.');
      return;
    }

    setIsSending(true);
    setSendError(null);

    try {
      const bodyText =
        clean || (pendingAttachments.length > 0 ? '📷 Sent an attachment' : '');
      await sendMessage(
        activeJob?.id || null,
        activeContactId,
        bodyText,
        pendingAttachments.length > 0 ? pendingAttachments : undefined,
        activeThread.conversationId
      );
      setInputText('');
      setPendingAttachments([]);
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 60);
    } catch (err: any) {
      console.error('Send message error:', err);
      setSendError(err.message || 'Failed to send message. Please check your connection.');
    } finally {
      setIsSending(false);
    }
  };

  if (!currentUser) return null;

  if (isUnauthorizedJob) {
    return (
      <div className="max-w-2xl mx-auto py-16 px-4 text-center space-y-4">
        <div className="w-16 h-16 bg-rose-50 rounded-2xl text-rose-600 flex items-center justify-center mx-auto">
          <AlertCircle className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-black text-slate-900">Unauthorized Chat Access</h2>
        <p className="text-sm text-slate-600">
          You are not authorized to view or send messages for this job request. You must be the customer who created this job or an eligible professional.
        </p>
        <button
          onClick={() =>
            navigateTo(
              currentUser?.role === 'professional' ? 'pro_dashboard' : 'customer_dashboard'
            )
          }
          className="px-6 py-2.5 rounded-xl bg-slate-900 text-white font-bold text-xs cursor-pointer min-h-[44px]"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-2.5 sm:py-6 space-y-2.5 sm:space-y-4">
      {/* Top Bar */}
      <div className="flex items-center justify-between gap-2 px-1">
        <button
          onClick={() =>
            currentUser?.role === 'customer'
              ? navigateTo('customer_dashboard')
              : currentUser?.role === 'admin'
              ? navigateTo('admin_dashboard')
              : navigateTo('pro_dashboard')
          }
          className="text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white flex items-center gap-1.5 min-h-[40px] cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Dashboard</span>
        </button>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setSidebarTab('contacts');
              setMobileView('list');
              setTimeout(() => searchInputRef.current?.focus(), 80);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-xs transition-colors cursor-pointer"
          >
            <Users className="w-3.5 h-3.5" />
            <span>Eligible Contacts</span>
          </button>
          <span className="hidden sm:inline-flex text-xs font-semibold text-emerald-800 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200 items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span>End-to-End Protected Messenger</span>
          </span>
        </div>
      </div>

      {/* Main WhatsApp/Telegram Split Container */}
      <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200 shadow-xl overflow-hidden grid grid-cols-1 md:grid-cols-12 h-[calc(100dvh-195px)] sm:h-[calc(100dvh-150px)] min-h-[420px] sm:min-h-[560px] max-h-[820px]">
        {/* LEFT PANE: Searchable Contact List & Recent Chats */}
        <div
          className={`md:col-span-4 lg:col-span-4 border-r border-slate-200 flex flex-col h-full bg-white ${
            mobileView === 'chat' ? 'hidden md:flex' : 'flex'
          }`}
        >
          {/* Sidebar Header */}
          <div className="p-3.5 sm:p-4 border-b border-slate-100 space-y-3 bg-slate-50/70">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="font-extrabold text-base text-slate-900 tracking-tight">
                  Messages
                </h2>
                {totalUnreadCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-600 text-white font-black text-[11px]">
                    {totalUnreadCount}
                  </span>
                )}
              </div>
              <span className="text-[11px] font-semibold text-slate-500">
                {unifiedThreads.length}{' '}
                {unifiedThreads.length === 1 ? 'contact' : 'contacts'}
              </span>
            </div>

            {/* Search Input */}
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                ref={searchInputRef}
                id="chat-contact-search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search name, trade, location, or chat..."
                className="w-full pl-9 pr-8 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-hidden focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Filter Tabs */}
            <div className="grid grid-cols-3 gap-1 p-1 bg-slate-200/70 rounded-xl text-xs font-bold">
              <button
                type="button"
                onClick={() => setSidebarTab('all')}
                className={`py-1.5 rounded-lg transition-all cursor-pointer ${
                  sidebarTab === 'all'
                    ? 'bg-white text-slate-900 shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Chats
              </button>
              <button
                type="button"
                onClick={() => setSidebarTab('unread')}
                className={`py-1.5 rounded-lg transition-all flex items-center justify-center gap-1 cursor-pointer ${
                  sidebarTab === 'unread'
                    ? 'bg-white text-slate-900 shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <span>Unread</span>
                {totalUnreadCount > 0 && (
                  <span className="w-4 h-4 rounded-full bg-emerald-600 text-white text-[10px] flex items-center justify-center">
                    {totalUnreadCount}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setSidebarTab('contacts')}
                className={`py-1.5 rounded-lg transition-all flex items-center justify-center gap-1 cursor-pointer ${
                  sidebarTab === 'contacts'
                    ? 'bg-white text-slate-900 shadow-2xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <span>Contacts</span>
              </button>
            </div>
          </div>

          {/* Scrollable Contact / Thread List */}
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {isLoadingContacts && unifiedThreads.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-500 space-y-2">
                <Loader2 className="w-6 h-6 text-emerald-600 animate-spin mx-auto" />
                <p>Loading your conversations and eligible contacts...</p>
              </div>
            ) : filteredThreads.length === 0 ? (
              <div className="p-8 text-center space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
                  <Users className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <p className="font-bold text-slate-800 text-xs sm:text-sm">
                    {sidebarTab === 'unread'
                      ? 'No unread messages'
                      : searchQuery
                      ? 'No matching contacts found'
                      : 'No eligible contacts yet'}
                  </p>
                  <p className="text-[11px] text-slate-500 max-w-xs mx-auto leading-relaxed">
                    {sidebarTab === 'unread'
                      ? 'You are all caught up on your messages.'
                      : isCustomer
                      ? 'Professionals you have hired, are actively working with, or have previously chatted with will appear here.'
                      : 'Customers who have hired you, are actively working with you, or have previously chatted with you will appear here.'}
                  </p>
                </div>
                {sidebarTab === 'unread' && unifiedThreads.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setSidebarTab('all');
                      setSearchQuery('');
                    }}
                    className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold shadow-xs cursor-pointer"
                  >
                    View All Conversations
                  </button>
                )}
              </div>
            ) : (
              filteredThreads.map((item) => {
                const isSelected = item.contactId === activeContactId;
                const lastMsg = item.lastMessage;
                const isLastFromMe = lastMsg?.senderId === currentUser.id;
                const roleOrTradeLabel =
                  item.contactRole === 'professional'
                    ? item.primaryCategory || 'Verified Professional'
                    : 'Customer';

                return (
                  <button
                    key={item.contactId}
                    type="button"
                    onClick={() => handleSelectContact(item)}
                    className={`w-full text-left px-3.5 py-3 transition-colors flex items-start gap-3 cursor-pointer ${
                      isSelected
                        ? 'bg-emerald-50/80 border-l-4 border-l-emerald-600'
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    {/* Avatar + Online Indicator */}
                    <div className="relative shrink-0 mt-0.5">
                      {item.contactAvatar ? (
                        <img
                          src={item.contactAvatar}
                          alt={item.contactName}
                          className="w-12 h-12 rounded-full object-cover border border-slate-200"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-600 to-teal-700 text-white flex items-center justify-center font-black text-base shadow-2xs">
                          {item.contactName.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span
                        title={
                          item.isOnline
                            ? 'Online now'
                            : item.isAvailable
                            ? 'Available'
                            : 'Offline'
                        }
                        className={`w-3.5 h-3.5 rounded-full border-2 border-white absolute bottom-0 right-0 ${
                          item.isOnline
                            ? 'bg-emerald-500'
                            : item.isAvailable
                            ? 'bg-amber-400'
                            : 'bg-slate-300'
                        }`}
                      />
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <div className="flex items-center gap-1 min-w-0">
                          <span className="font-bold text-xs sm:text-sm text-slate-900 truncate">
                            {item.contactName}
                          </span>
                          {item.contactRole === 'professional' && item.isVerified && (
                            <VerificationBadge status="verified" size="sm" />
                          )}
                        </div>
                        {lastMsg ? (
                          <span
                            className={`text-[10px] shrink-0 ${
                              item.unreadCount > 0
                                ? 'font-extrabold text-emerald-600'
                                : 'text-slate-400 font-medium'
                            }`}
                          >
                            {formatChatListTime(lastMsg.createdAt)}
                          </span>
                        ) : item.isOnline ? (
                          <span className="text-[10px] font-bold text-emerald-600 shrink-0">
                            Online
                          </span>
                        ) : null}
                      </div>

                      {/* Trade / Role & Location Subtitle */}
                      <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-slate-500 truncate">
                        <span
                          className={`inline-flex items-center gap-1 font-semibold px-1.5 py-0.2 rounded text-[10px] ${
                            item.contactRole === 'professional'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/70'
                              : 'bg-blue-50 text-blue-700 border border-blue-200/70'
                          }`}
                        >
                          {item.contactRole === 'professional' ? (
                            <Wrench className="w-2.5 h-2.5" />
                          ) : (
                            <UserCheck className="w-2.5 h-2.5" />
                          )}
                          <span className="truncate max-w-[130px]">{roleOrTradeLabel}</span>
                        </span>
                        {(item.lga || item.state) && (
                          <span className="truncate text-[10px] text-slate-400">
                            • {[item.lga, item.state].filter(Boolean).join(', ')}
                          </span>
                        )}
                      </div>

                      {/* Last Message Preview or Start Direct Chat Prompt */}
                      <div className="flex items-center justify-between gap-2 mt-1">
                        {lastMsg ? (
                          <p
                            className={`text-xs truncate flex items-center gap-1 ${
                              item.unreadCount > 0
                                ? 'font-bold text-slate-900'
                                : 'text-slate-500'
                            }`}
                          >
                            {isLastFromMe && (
                              <span className="shrink-0">
                                {lastMsg.read ? (
                                  <CheckCheck className="w-3.5 h-3.5 text-emerald-600 inline" />
                                ) : (
                                  <CheckCheck className="w-3.5 h-3.5 text-slate-400 inline" />
                                )}
                              </span>
                            )}
                            <span className="truncate">{lastMsg.content}</span>
                          </p>
                        ) : item.primaryJob ? (
                          <p className="text-[11px] text-emerald-700 font-medium truncate flex items-center gap-1">
                            <Briefcase className="w-3 h-3 shrink-0" />
                            <span className="truncate">Job: {item.primaryJob.title}</span>
                          </p>
                        ) : (
                          <p className="text-[11px] text-slate-400 italic truncate">
                            Tap to start a direct chat
                          </p>
                        )}

                        {item.unreadCount > 0 && (
                          <span className="px-1.5 py-0.5 min-w-[20px] rounded-full bg-emerald-600 text-white font-black text-[10px] text-center shrink-0">
                            {item.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT PANE: Active Conversation Stream */}
        <div
          className={`md:col-span-8 lg:col-span-8 flex flex-col h-full bg-slate-50/50 ${
            mobileView === 'list' ? 'hidden md:flex' : 'flex'
          }`}
        >
          {activeThread ? (
            <>
              {/* Active Chat Header */}
              <div className="px-3.5 py-3 sm:px-5 sm:py-3.5 border-b border-slate-200 bg-white flex items-center justify-between gap-2 shadow-2xs z-10">
                <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                  {/* Mobile Back Button to Contact List */}
                  <button
                    type="button"
                    onClick={() => setMobileView('list')}
                    className="md:hidden p-2 -ml-1 rounded-xl text-slate-600 hover:bg-slate-100 cursor-pointer"
                    aria-label="Back to contact list"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>

                  {/* Avatar */}
                  <div className="relative shrink-0">
                    {activeThread.contactAvatar ? (
                      <img
                        src={activeThread.contactAvatar}
                        alt={activeThread.contactName}
                        className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-cover border border-slate-200"
                      />
                    ) : (
                      <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-gradient-to-br from-emerald-600 to-teal-700 text-white flex items-center justify-center font-black text-sm shadow-2xs">
                        {activeThread.contactName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span
                      className={`w-3 h-3 rounded-full border-2 border-white absolute bottom-0 right-0 ${
                        activeThread.isOnline
                          ? 'bg-emerald-500'
                          : activeThread.isAvailable
                          ? 'bg-amber-400'
                          : 'bg-slate-300'
                      }`}
                    />
                  </div>

                  {/* Contact Name, Trade & Online Status */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <h3 className="font-extrabold text-sm sm:text-base text-slate-900 truncate">
                        {activeThread.contactName}
                      </h3>
                      {activeThread.contactRole === 'professional' &&
                        activeThread.isVerified && (
                          <VerificationBadge status="verified" size="sm" />
                        )}
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          activeThread.contactRole === 'professional'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-blue-50 text-blue-700 border border-blue-200'
                        }`}
                      >
                        {activeThread.contactRole === 'professional'
                          ? activeThread.primaryCategory || 'Verified Artisan'
                          : 'Customer'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-[11px] text-slate-500 truncate mt-0.5">
                      <span
                        className={`font-semibold flex items-center gap-1 ${
                          activeThread.isOnline ? 'text-emerald-600' : 'text-slate-500'
                        }`}
                      >
                        {activeThread.isOnline
                          ? 'Online'
                          : activeThread.isAvailable
                          ? 'Available for chat'
                          : 'Offline'}
                      </span>
                      {(activeThread.lga || activeThread.state) && (
                        <>
                          <span>•</span>
                          <span className="truncate flex items-center gap-0.5">
                            <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                            {[activeThread.lga, activeThread.state]
                              .filter(Boolean)
                              .join(', ')}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Action Buttons (Profile, Protected Phone, Request Quote) */}
                <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                  {activeThread.contactRole === 'professional' && (
                    <button
                      type="button"
                      onClick={() =>
                        navigateTo('pro_profile', { proId: activeThread.contactId })
                      }
                      className="hidden sm:inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-colors cursor-pointer"
                    >
                      <span>View Profile</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  )}

                  {isCustomer &&
                    activeThread.contactRole === 'professional' &&
                    !activeJob && (
                      <button
                        type="button"
                        onClick={() =>
                          navigateTo('post_job', {
                            selectedCategory: activeThread.primaryCategory,
                            targetProfessionalId: activeThread.contactId,
                            targetProfessionalName: activeThread.contactName,
                            targetProfessionalBusiness:
                              activeThread.businessName || activeThread.primaryCategory,
                            targetProfessionalAvatar: activeThread.contactAvatar,
                          })
                        }
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 text-xs font-bold transition-colors cursor-pointer"
                        title="Create a job request for this professional"
                      >
                        <Briefcase className="w-3.5 h-3.5 text-emerald-600" />
                        <span className="hidden sm:inline">Hire / Post Job</span>
                      </button>
                    )}

                  {activeThread.phone && !activeThread.phoneProtected ? (
                    <a
                      href={`tel:${activeThread.phone}`}
                      className="p-2.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center cursor-pointer"
                      title={`Call ${activeThread.phone}`}
                    >
                      <Phone className="w-4 h-4" />
                    </a>
                  ) : (
                    <div
                      className="p-2 rounded-xl bg-slate-100 text-slate-400 flex items-center gap-1 text-[10px] font-semibold"
                      title="Private phone numbers remain protected until a job is assigned or funded in escrow."
                    >
                      <Lock className="w-3.5 h-3.5" />
                      <span className="hidden lg:inline">Phone Protected</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Linked Job Context Banner (if job-linked or shared jobs exist) */}
              {(activeJob || activeThread.sharedJobs.length > 0) && (
                <div className="px-4 py-2.5 bg-emerald-50/90 border-b border-emerald-200/80 flex flex-wrap items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <Briefcase className="w-4 h-4 text-emerald-700 shrink-0" />
                    {activeJob ? (
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <span className="font-bold text-slate-900 truncate">
                          Job: {activeJob.title}
                        </span>
                        <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-white text-emerald-800 border border-emerald-200 capitalize">
                          {activeJob.status === 'assigned'
                            ? 'In Progress'
                            : activeJob.status}
                        </span>
                        {isEscrowVerified && (
                          <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-emerald-700 text-white flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" />
                            Escrow Funded
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-700 font-medium">
                        Direct Chat (You also have {activeThread.sharedJobs.length} shared
                        job{activeThread.sharedJobs.length === 1 ? '' : 's'})
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {activeThread.sharedJobs.length > 1 && (
                      <select
                        value={selectedJobId || ''}
                        onChange={(e) => setSelectedJobId(e.target.value || null)}
                        className="text-[11px] bg-white border border-emerald-200 rounded-lg px-2 py-1 font-semibold text-slate-700"
                      >
                        <option value="">Direct Chat (All)</option>
                        {activeThread.sharedJobs.map((j) => (
                          <option key={j.id} value={j.id}>
                            {j.title} ({j.status})
                          </option>
                        ))}
                      </select>
                    )}
                    {activeJob && (
                      <button
                        type="button"
                        onClick={() => navigateTo('job_details', { jobId: activeJob.id })}
                        className="text-[11px] font-bold text-emerald-800 hover:underline flex items-center gap-1 cursor-pointer"
                      >
                        <span>Job Details</span>
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Chat Message History Stream */}
              <div
                ref={chatContainerRef}
                className="flex-1 p-3.5 sm:p-5 space-y-3 overflow-y-auto bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:16px_16px]"
              >
                {/* Security Notice Pill */}
                <div className="max-w-md mx-auto px-3.5 py-2 rounded-2xl bg-amber-50/95 border border-amber-200/80 text-center text-[11px] text-amber-900 shadow-2xs">
                  <p className="font-semibold flex items-center justify-center gap-1">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                    <span>
                      Messages are protected. Keep payments inside JobMatch NG Escrow for full buyer & artisan protection.
                    </span>
                  </p>
                </div>

                {conversationMessages.length === 0 ? (
                  <div className="text-center py-12 px-4 space-y-3">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-100/80 text-emerald-700 flex items-center justify-center mx-auto shadow-2xs">
                      <Sparkles className="w-6 h-6" />
                    </div>
                    <div className="space-y-1">
                      <p className="font-extrabold text-slate-900 text-sm">
                        Say hello to {activeThread.contactName}
                      </p>
                      <p className="text-slate-500 max-w-xs mx-auto text-xs leading-relaxed">
                        You can chat directly to ask questions, discuss availability, share photos of the fault, or coordinate a job.
                      </p>
                    </div>
                  </div>
                ) : (
                  conversationMessages.map((msg, idx) => {
                    const isMine = msg.senderId === currentUser.id;
                    const prevMsg = idx > 0 ? conversationMessages[idx - 1] : null;
                    const currentDateLabel = formatMessageDateDivider(msg.createdAt);
                    const prevDateLabel = prevMsg
                      ? formatMessageDateDivider(prevMsg.createdAt)
                      : '';
                    const showDateDivider = currentDateLabel !== prevDateLabel;
                    const attachments = Array.isArray(msg.attachments)
                      ? msg.attachments.filter(Boolean)
                      : [];

                    return (
                      <React.Fragment key={msg.id}>
                        {showDateDivider && currentDateLabel && (
                          <div className="flex justify-center my-2">
                            <span className="px-3 py-1 rounded-full bg-white/90 border border-slate-200 text-[10px] font-bold text-slate-500 shadow-2xs">
                              {currentDateLabel}
                            </span>
                          </div>
                        )}

                        <div
                          className={`flex flex-col ${
                            isMine ? 'items-end' : 'items-start'
                          }`}
                        >
                          <div
                            className={`max-w-[85%] sm:max-w-md px-3.5 py-2.5 rounded-2xl text-xs sm:text-sm leading-relaxed shadow-xs ${
                              isMine
                                ? 'bg-emerald-600 text-white rounded-br-xs'
                                : 'bg-white text-slate-800 border border-slate-200/90 rounded-bl-xs'
                            }`}
                          >
                            {/* Attachments inside message bubble */}
                            {attachments.length > 0 && (
                              <div className="grid grid-cols-1 gap-1.5 mb-2">
                                {attachments.map((url, aIdx) => (
                                  <button
                                    key={`${msg.id}-att-${aIdx}`}
                                    type="button"
                                    onClick={() => setPreviewImageUrl(url)}
                                    className="block rounded-xl overflow-hidden border border-black/10 cursor-pointer"
                                  >
                                    <img
                                      src={url}
                                      alt="Chat attachment"
                                      className="max-h-56 w-full object-cover hover:opacity-95 transition-opacity"
                                    />
                                  </button>
                                ))}
                              </div>
                            )}

                            {msg.content && (
                              <p className="whitespace-pre-wrap break-words">
                                {msg.content}
                              </p>
                            )}

                            {/* Timestamp + Delivery / Read Receipt */}
                            <div
                              className={`flex items-center justify-end gap-1 mt-1 text-[10px] ${
                                isMine ? 'text-emerald-100' : 'text-slate-400'
                              }`}
                            >
                              <span>
                                {new Date(msg.createdAt).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                              {isMine && (
                                <span
                                  title={
                                    msg.read
                                      ? `Read${
                                          msg.readAt
                                            ? ` at ${new Date(
                                                msg.readAt
                                              ).toLocaleTimeString([], {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                              })}`
                                            : ''
                                        }`
                                      : 'Delivered'
                                  }
                                >
                                  {msg.read ? (
                                    <CheckCheck className="w-3.5 h-3.5 text-sky-200" />
                                  ) : msg.delivered !== false ? (
                                    <CheckCheck className="w-3.5 h-3.5 text-emerald-200/80" />
                                  ) : (
                                    <Check className="w-3.5 h-3.5 text-emerald-200/80" />
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Pending Attachment Previews */}
              {pendingAttachments.length > 0 && (
                <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-200 flex items-center gap-2 overflow-x-auto">
                  {pendingAttachments.map((url, idx) => (
                    <div
                      key={`pending-${idx}`}
                      className="relative w-14 h-14 rounded-xl overflow-hidden border border-slate-300 shrink-0"
                    >
                      <img
                        src={url}
                        alt="Pending upload"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setPendingAttachments((prev) =>
                            prev.filter((_, i) => i !== idx)
                          )
                        }
                        className="w-5 h-5 rounded-full bg-slate-900/80 text-white flex items-center justify-center absolute top-0.5 right-0.5 cursor-pointer"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Error feedback banner */}
              {sendError && (
                <div className="p-2.5 bg-rose-50 border-t border-rose-200 text-rose-700 text-xs flex items-center justify-between px-4">
                  <div className="flex items-center gap-1.5">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{sendError}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSendError(null)}
                    className="text-xs font-bold text-rose-900 hover:underline cursor-pointer"
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {/* Message Composer */}
              <form
                onSubmit={handleSend}
                className="p-3 sm:p-3.5 border-t border-slate-200 bg-white"
              >
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleAttachmentSelect}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSending || isUploadingAttachment}
                    title="Attach photo"
                    className="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer disabled:opacity-50"
                  >
                    {isUploadingAttachment ? (
                      <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                    ) : (
                      <Paperclip className="w-4 h-4" />
                    )}
                  </button>

                  <input
                    type="text"
                    id="chat-message-input"
                    placeholder={`Message ${activeThread.contactName}...`}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    disabled={isSending}
                    className="flex-1 px-3.5 sm:px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-hidden focus:border-emerald-500 min-h-[44px] disabled:bg-slate-50"
                  />

                  <button
                    type="submit"
                    id="send-chat-btn"
                    disabled={
                      isSending ||
                      (!inputText.trim() && pendingAttachments.length === 0)
                    }
                    className="p-3 sm:px-5 sm:py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-bold text-xs shadow-xs transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center gap-1.5 cursor-pointer disabled:cursor-not-allowed"
                  >
                    {isSending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span className="hidden sm:inline">Send</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="p-8 text-center my-auto space-y-3">
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto">
                <MessageSquare className="w-7 h-7" />
              </div>
              <h4 className="font-extrabold text-slate-900 text-base">
                Select a conversation to start or continue chatting
              </h4>
              <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
                {isCustomer
                  ? 'Select any professional you have hired, are actively working with, or have previously chatted with from the contact list on the left.'
                  : 'Select any customer who hired you, is actively working with you, or has previously chatted with you from the contact list on the left.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox Modal for Attachment Preview */}
      {previewImageUrl && (
        <div
          onClick={() => setPreviewImageUrl(null)}
          className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-4"
        >
          <div className="relative max-w-3xl max-h-[85vh]">
            <button
              type="button"
              onClick={() => setPreviewImageUrl(null)}
              className="absolute -top-10 right-0 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={previewImageUrl}
              alt="Full attachment preview"
              className="max-w-full max-h-[82vh] rounded-2xl object-contain shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};
